import { useState } from 'react';
import { useAuth } from '../contexts/auth/useAuth';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import { useAcceptTerms } from '../hooks/api/useTerms';
import { ModalDialog } from './ModalDialog';
import { PageTitle } from './PageTitle';

const sectionTitleSx = { fontWeight: 700, mt: 24, mb: 12 };
const paragraphSx = { mb: 16 };
const listSx = { mb: 16, pl: 24, listStyleType: 'disc', '& li': { mb: 8 } };

/**
 * The trial terms of use, for users of a trial account who haven't accepted
 * them yet. It can't be dismissed: the only ways out are Accept & Continue and
 * Decline, which logs out. The terms are written in English, hence `lang="en"`,
 * and the checkbox is the last thing in the scroll box, so it is only reached
 * after scrolling through them.
 */
export const TrialTermsOverlay = () => {
  const { logout } = useAuth();
  const acceptTerms = useAcceptTerms();
  const [hasAccepted, setHasAccepted] = useState(false);
  const [error, setError] = useState(false);

  const handleAccept = async () => {
    setError(false);
    try {
      await acceptTerms.mutateAsync();
    } catch {
      setError(true);
    }
  };

  const handleDecline = () => {
    localStorage.clear();
    logout();
  };

  return (
    <>
      <PageTitle title="Terms of Use" />
      <ModalDialog
        open
        // Neither Escape nor a click outside closes it.
        onClose={() => {}}
        title="Terms of Use"
        subtitle="Please read and accept before accessing the free JST trial"
        error={
          error
            ? 'Something went wrong accepting the terms. Please try again.'
            : undefined
        }
        actions={
          <>
            <Button
              variant="default"
              onClick={handleDecline}
              disabled={acceptTerms.isPending}
            >
              Decline
            </Button>
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={!hasAccepted || acceptTerms.isPending}
              startIcon={
                acceptTerms.isPending ? <CircularProgress size={16} /> : null
              }
            >
              {acceptTerms.isPending ? 'Accepting…' : 'Accept & Continue'}
            </Button>
          </>
        }
      >
        <Box
          lang="en"
          role="region"
          tabIndex={0}
          aria-label="Terms of Use text"
          sx={{
            p: 16,
            maxHeight: 300,
            overflowY: 'auto',
            borderRadius: 1,
            backgroundColor: 'var(--color-light-greyish-blue)',
            fontSize: 14,
            '&:focus-visible': {
              outline: '2px solid var(--color-scifree-teal)',
              outlineOffset: '2px',
            },
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 16 }}>
            SciFree AB. Journal Search Tool (JST)
          </Typography>

          <Box
            sx={{
              p: 12,
              mb: 16,
              borderRadius: 1,
              backgroundColor: 'var(--color-very-pale-orange)',
              border: '1px solid var(--color-deep-ocean-blue)',
            }}
          >
            <Typography variant="body1" fontWeight={700} mb={4}>
              Important – Intellectual Property
            </Typography>
            <Typography>
              Access to this trial is granted for evaluation purposes only. You
              may not copy, reproduce, or build a competing version of this tool
              or any part of it during or after your trial period.
            </Typography>
          </Box>

          <Typography variant="h4" sx={sectionTitleSx}>
            1. Your trial access
          </Typography>
          <Typography sx={paragraphSx}>
            SciFree AB (&quot;SciFree&quot;) grants you time-limited,
            non-exclusive access to the Journal Search Tool (&quot;the
            Tool&quot;) for internal evaluation purposes only. Access begins
            when SciFree activates your account and ends at the close of the
            agreed trial period.
          </Typography>
          <Typography sx={paragraphSx}>
            Trial access does not constitute a commercial licence and confers no
            ongoing rights to the Tool.
          </Typography>

          <Typography variant="h4" sx={sectionTitleSx}>
            2. Permitted and prohibited use
          </Typography>
          <Typography variant="body1" fontWeight={600} mb={8}>
            During your trial you may:
          </Typography>
          <Box component="ul" sx={listSx}>
            <li>
              Access and evaluate the Tool for your institution&apos;s internal
              purposes.
            </li>
            <li>
              Share access with colleagues at your institution for evaluation.
            </li>
            <li>
              Use SciFree&apos;s brand when communicating the trial to your own
              users.
            </li>
          </Box>
          <Typography variant="body1" fontWeight={600} mb={8}>
            You may not:
          </Typography>
          <Box component="ul" sx={listSx}>
            <li>
              Copy, reproduce, reverse-engineer, or replicate any part of the
              Tool or its underlying technology.
            </li>
            <li>
              Create or assist in creating a product or service that competes
              with the Tool, during or for twenty-four (24) months after it
              ends.
            </li>
            <li>
              Share, sublicense, or transfer your trial access to any party
              outside your institution.
            </li>
            <li>
              Use the Tool for any purpose contrary to applicable law or
              regulation.
            </li>
          </Box>

          <Typography variant="h4" sx={sectionTitleSx}>
            3. Ownership and intellectual property
          </Typography>
          <Typography sx={paragraphSx}>
            The Tool, its design, underlying technology, and all associated
            materials remain the sole property of SciFree AB. Nothing in this
            trial grants you any ownership rights or licences beyond those
            expressly stated here.
          </Typography>
          <Typography sx={paragraphSx}>
            Your institution retains ownership of any data or journal
            information you import into the Tool during the trial.
          </Typography>

          <Typography variant="h4" sx={sectionTitleSx}>
            4. Confidentiality
          </Typography>
          <Typography sx={paragraphSx}>
            Information about the Tool&apos;s functionality, design, and
            business model that you access during the trial is confidential. You
            agree not to disclose this information to third parties during your
            trial or for twenty-four (24) months after it ends.
          </Typography>
          <Typography sx={paragraphSx}>
            Please ensure that colleagues with trial access are aware of this
            obligation.
          </Typography>

          <Typography variant="h4" sx={sectionTitleSx}>
            5. Limitation of liability
          </Typography>
          <Typography sx={paragraphSx}>
            The Tool is provided &quot;as is&quot; for evaluation purposes.
            SciFree makes no warranties regarding the Tool&apos;s fitness for
            any particular purpose during the trial period. SciFree&apos;s
            liability is limited to direct damages only and does not extend to
            any indirect, consequential, or financial losses.
          </Typography>

          <Typography variant="h4" sx={sectionTitleSx}>
            6. Governing terms and contact
          </Typography>
          <Typography sx={paragraphSx}>
            These trial terms are governed by Swedish law. Any disputes will be
            resolved through open and honest conversation in the first instance.
          </Typography>
          <Box
            sx={{
              mt: 24,
              pt: 16,
              borderTop: '1px solid var(--color-mid-greyish-blue)',
            }}
          >
            <Typography sx={paragraphSx}>
              Questions? Contact us at{' '}
              <Link href="mailto:info@scifree.se">info@scifree.se</Link>.
            </Typography>
            <Typography sx={paragraphSx}>
              By clicking <strong>Accept &amp; Continue</strong> you confirm
              that you have read these terms and agree to be bound by them on
              behalf of your institution. If you do not agree, click{' '}
              <strong>Decline</strong> and your trial access will not be
              activated.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={hasAccepted}
                  onChange={(e) => setHasAccepted(e.target.checked)}
                  inputProps={{ 'aria-required': true }}
                />
              }
              label="I have read and agree to the Terms of Use on behalf of my institution"
            />
          </Box>
        </Box>
      </ModalDialog>
    </>
  );
};
